import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { useMemo } from "react";
import { Controller, Control } from "react-hook-form";
import { SubmissionReviewState } from "hooks/submission/reviewStates";

interface ReviewFormValues {
  reviewFinished: boolean;
}

interface SubmissionDisplayReviewOrSignDocumentProps {
  isReviewerASigner: boolean;
  reviewState: SubmissionReviewState;
  reviewFinished: boolean;
  submissionDocumentReviewId: string | null;
  signingCompleted: boolean;
  isMutating: boolean;
  control: Control<ReviewFormValues>;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
}

export function SubmissionDisplayReviewOrSignDocument({
  isReviewerASigner,
  reviewState,
  reviewFinished,
  submissionDocumentReviewId,
  signingCompleted,
  isMutating,
  control,
  onSubmit,
}: SubmissionDisplayReviewOrSignDocumentProps) {
  const isSubmitDisabled = useMemo(() => {
    // Form is disabled to signer but it will be disabled
    if (reviewState === SubmissionReviewState.SIGNER_IS_WAITING_FOR_REVIEWERS) {
      return true;
    }
    if (reviewState === SubmissionReviewState.SIGNER_CAN_SIGN) {
      // if they have selected Approve but have not signed the document, they should not be able to submit
      if (reviewFinished && !signingCompleted) {
        return true;
      }
    }
    return !submissionDocumentReviewId || isMutating;
  }, [
    reviewFinished,
    signingCompleted,
    submissionDocumentReviewId,
    isMutating,
    reviewState,
  ]);

  return (
    <Grid size={{ xs: 12, md: 6 }}>
      <Stack spacing={2} sx={{ height: "100%" }}>
        <Box
          component="form"
          onSubmit={onSubmit}
          sx={{
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
          }}
        >
          <FormControl
            component="fieldset"
            disabled={
              reviewState ===
              SubmissionReviewState.SIGNER_IS_WAITING_FOR_REVIEWERS
            }
            sx={{ mb: 3 }}
          >
            <FormLabel component="legend">
              {isReviewerASigner ? "Sign complete" : "Review submission"}
            </FormLabel>
            <Controller
              name="reviewFinished"
              control={control}
              render={({ field }) => (
                <RadioGroup
                  {...field}
                  value={field.value ? "true" : "false"}
                  onChange={(event) =>
                    field.onChange(event.target.value === "true")
                  }
                >
                  <FormControlLabel
                    value="true"
                    control={<Radio />}
                    label={`✅ ${
                      isReviewerASigner
                        ? "Done signing, ready for submission"
                        : "Good to go, ready for signature"
                    }`}
                  />
                  <FormControlLabel
                    value="false"
                    control={<Radio />}
                    label="🔄 Needs edits, specified in comments"
                  />
                </RadioGroup>
              )}
            />
          </FormControl>

          {submissionDocumentReviewId && !isReviewerASigner && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {reviewFinished
                ? "We will approve this review and move it forward for signature."
                : "We will send this back for edits based on your comments. After sending this back for edits you will no longer be able to view it."}
            </Typography>
          )}

          {!submissionDocumentReviewId && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Review actions will be available once a document review is
              assigned.
            </Typography>
          )}

          <Box
            sx={{
              mt: "auto",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitDisabled}
            >
              {isReviewerASigner ? "Finish" : "Review complete"}
            </Button>
          </Box>
        </Box>
      </Stack>
    </Grid>
  );
}
